import React from 'react'
import type { TooltipProps } from '@mui/material'
import { Box, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { useIntl } from 'react-intl'
import { PersonPinOutlined } from '@mui/icons-material'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { roleSupportsOwnerRestriction, solutionSupportsOwnerRestriction } from '@app/src/utils/rolePermissions'

type OwnerRestrictionFieldProps = {
  control: Control
  name: string
  selectedRole: string
  solution: string | undefined
} & Omit<TooltipProps, 'title' | 'children'>

export function OwnerRestrictionField({ control, name, selectedRole, solution, ...props }: OwnerRestrictionFieldProps) {
  const { formatMessage } = useIntl()

  const solutionSupport = solutionSupportsOwnerRestriction(solution)
  const notSupported = !roleSupportsOwnerRestriction(selectedRole)

  // Don't show at all if editing a user in not supported solution
  if (!solutionSupport) {
    return null
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ onChange, value }): JSX.Element => (
        <Tooltip
          arrow
          placement="top"
          disableHoverListener={!notSupported}
          {...props}
          title={formatMessage({ id: `permissions.ownerRestricted.notAvailableExplanation` })}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              cursor: 'default',
              opacity: notSupported ? 0.5 : 1,
            }}
          >
            <PersonPinOutlined />
            <Box>
              <Typography variant="subtitle1" color="text.primary">
                {formatMessage({ id: `permissions.ownerRestricted.name` })}
              </Typography>
              <Typography variant="body2" color="text.primary">
                {formatMessage({ id: `permissions.ownerRestricted.description` })}
              </Typography>
            </Box>

            <Switch checked={!notSupported && value} onChange={() => onChange(!value)} disabled={notSupported} />
          </Stack>
        </Tooltip>
      )}
    />
  )
}
